// src/pages/ClientRolesPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { staffApi } from "../Apis/Api";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { formatDateTime } from "../utils/formatDateTime";
import { TextWrap } from "../utils/textWrap";
import PaginationControls from "../components/ui/PaginationControls";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { AddButton } from "../components/ui/Button";
import { handleApiResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal";

const ClientRolesPage = () => {
  const navigate = useNavigate();
  const [roleData, setRoleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false);
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    fetchRoleData();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  const fetchRoleData = async () => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        name: "role_name",
        role_name: "role_name",
        description: "description",
        is_active: "is_active",
        created_by: "created_by",
        created_datetime: "created_datetime",
      };

      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(filters.status && {
          is_active: filters.status === "Active" ? true : false,
        }),
        ...(sortConfig.field && {
          sort_by: fieldMapping[sortConfig.field] || sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };

      const response = await staffApi.getAllClientRoles(params);

      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: data is not an array");
      }
      if (!response.pagination || typeof response.pagination !== "object") {
        throw new Error("Invalid API response: pagination data missing");
      }

      let data = response.data.map((role, index) => ({
        id: role.client_role_id || index + 1,
        role_name: role.role_name || "-",
        name: role.role_name || "-",
        description: role.description || "-",
        is_active:
          role.is_active !== undefined && role.is_active !== null
            ? role.is_active
              ? "Active"
              : "Inactive"
            : "-",
        created_by: role.created_by || "-",
        created_datetime: role.created_datetime || "-",
        display_created_datetime: role.created_datetime
          ? formatDateTime(role.created_datetime)
          : "-",
      }));

      if (sortConfig.field) {
        data = [...data].sort((a, b) => {
          const valueA = a[sortConfig.field] || "";
          const valueB = b[sortConfig.field] || "";
          if (sortConfig.field === "created_datetime") {
            const dateA = new Date(a.created_datetime || 0);
            const dateB = new Date(b.created_datetime || 0);
            return sortConfig.direction === "asc"
              ? dateA - dateB
              : dateB - dateA;
          }
          return sortConfig.direction === "asc"
            ? valueA.toString().localeCompare(valueB.toString(), undefined, {
                sensitivity: "base",
              })
            : valueB.toString().localeCompare(valueA.toString(), undefined, {
                sensitivity: "base",
              });
        });
      }

      setRoleData(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || data.length,
      }));
      setError(null);
      handleApiResponse(response, "success", "GET");
    } catch (err) {
      const errorMessage =
        err?.message ||
        "Failed to fetch client roles. Please check the API endpoint.";
      setError(errorMessage);
      setRoleData([]);
      handleApiResponse(err, "Err", "GET");
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (role) => {
    navigate("/staff-role-edit", { state: { roleData: role } });
  };

  const handleDeleteClick = (roleId, roleName) => {
    setRoleToDelete({ id: roleId, name: roleName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (roleToDelete) {
      setLoading(true);
      try {
        const res = await staffApi.deleteClientRole(roleToDelete.id);
        handleApiResponse(res, "success", "DELETE");

        setRoleData((prev) =>
          prev.filter((role) => role.id !== roleToDelete.id)
        );
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          currentPage: Math.min(
            prev.currentPage,
            Math.ceil((prev.total - 1) / prev.perPage) || 1
          ),
        }));
        setShowDeleteModal(false);
        setRoleToDelete(null);
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || "Failed to delete role";
        handleApiResponse(err, "ERR", "DELETE");
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
        setRoleToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRoleToDelete(null);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleAddStaffWithRole = (role) => {
    navigate("/staff-create", {
      state: {
        selectedRole: { value: role.id, label: role.role_name },
        navigationLink: { link: "staff", isLink: true },
      },
    });
  };

  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchRoleData} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by Role Name or Description"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search roles by name or description"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Client Roles Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add New Role"
              permissionKey="add_staff_role"
              onClick={() => navigate("/staff-role-create")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Role List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {roleData.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No roles match your search or filter."
                  : "No client roles available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("role_name")}
                    role="button"
                    aria-label={`Sort by role name, currently sorted ${
                      sortConfig.field === "role_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Role Name
                      <SortIcons field="role_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("description")}
                    role="button"
                    aria-label={`Sort by description, currently sorted ${
                      sortConfig.field === "description"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Description
                      <SortIcons field="description" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_datetime")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_datetime"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="created_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {roleData.map((role, index) => (
                  <tr key={role.id}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {role.role_name}
                      </span>
                    </td>
                    <TextWrap location={role.description} />
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {role.created_by}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={role.is_active} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {role.display_created_datetime}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("add_staff") && (
                          <button
                            type="button"
                            onClick={() => handleAddStaffWithRole(role)}
                            className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Add staff with role ${role.role_name}`}
                            aria-label={`Add staff with role ${role.role_name}`}
                          >
                            <Icon
                              icon="lucide:user-plus"
                              className="menu-icon"
                            />
                          </button>
                        )}
                        {hasPermission("update_staff_role") && (
                          <button
                            type="button"
                            onClick={() => handleEdit(role)}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Edit role ${role.role_name}`}
                            aria-label={`Edit role ${role.role_name}`}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}

                        {hasPermission("delete_staff_role") && (
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteClick(role.id, role.role_name)
                            }
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Delete role ${role.role_name}`}
                            aria-label={`Delete role ${role.role_name}`}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none" ,backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
}}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>{roleToDelete?.name || "this role"}</strong>?
              </p>
              <p className="text-neutral-900 small">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                aria-label={`Confirm delete role ${roleToDelete?.name || ""}`}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}
      <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Roles & Permissions Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This page serves as the <strong>central dashboard</strong> for managing all <strong>User Roles</strong> within the system. 
    It allows administrators to create, view, edit, and delete roles, as well as quickly onboard new staff members to any selected role.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">Key Features</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">Display all existing roles with their name and description</li>
    <li className="list-group-item">Create unlimited custom roles tailored to your business</li>
    <li className="list-group-item">Edit role details or delete roles (if no staff is assigned)</li>
    <li className="list-group-item">Add new staff members directly from any role with one click</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Add New Role – Available Fields</h6>
  <p className="small text-neutral-600 mb-2">Clicking the “Add New Role” button opens a form containing:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Role Name</strong> – Unique identifier (e.g., “Branch Manager”, “Sales Executive”)</li>
    <li className="list-group-item"><strong>Description</strong> – Brief explanation of what the role entails</li>
    <li className="list-group-item"><strong>Status</strong> – Active / Inactive (optional, if implemented)</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Three Action Buttons (Per Role)</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Add Staff (Green Person/Add Icon)</strong><br/>
      <span className="text-success-600 fw-bold">Fastest Onboarding Method</span><br/>
      Opens a staff creation form with the selected role pre-filled:<br/>
      → Full Name | Email | Phone Number | Role (auto-selected) | Password
    </li>
    <li className="list-group-item">
      <strong>Edit (Pencil Icon)</strong><br/>
      Modify the role name or description at any time
    </li>
    <li className="list-group-item">
      <strong>Delete (Trash Icon)</strong><br/>
      Permanently remove the role<br/>
      <small className="text-danger">Disabled if any staff member is currently assigned</small>
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Important Notes:</strong><br/>
    • System-default roles (e.g., Super Admin) cannot be edited or deleted<br/>
    • Always use clear, descriptive role names for easier team management<br/>
    • After creating a role, use the “Add Staff” button immediately to assign users
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Create Role → Click “Add Staff” → Instantly onboard new team members with the correct permissions!
  </p>

  <p className="text-neutral-900 small">
    This streamlined workflow ensures full control over access levels while making staff management fast and error-free.
  </p>
</InfoModal>
    </>
  );
};

export default ClientRolesPage;